import type { StoredMeetingSummary } from '../pages/meeting/types/minutes.types.ts';

const DB_NAME = 'tbkmeet_summaries';
const DB_VERSION = 1;
const STORE_NAME = 'summaries';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export async function saveSummary(summary: StoredMeetingSummary): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE_NAME).put(summary);
  });
}

/** Aynı toplantı kodu için tek tutanak tutar (çıkışta mükerrer kayıt önlenir). */
export async function saveSummaryForMeeting(
  summary: StoredMeetingSummary
): Promise<void> {
  const existing = await listSummaries();
  const duplicates = existing.filter(
    (s) => s.meetingCode === summary.meetingCode && s.id !== summary.id
  );
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const d of duplicates) store.delete(d.id);
    store.put(summary);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listSummaries(): Promise<StoredMeetingSummary[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => {
      const items = (request.result as StoredMeetingSummary[]).sort(
        (a, b) => b.generatedAt - a.generatedAt
      );
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getSummary(id: string): Promise<StoredMeetingSummary | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(id);
    request.onsuccess = () => resolve((request.result as StoredMeetingSummary) || null);
    request.onerror = () => reject(request.error);
  });
}

export async function updateSummary(
  id: string,
  patch: Partial<StoredMeetingSummary>
): Promise<void> {
  const existing = await getSummary(id);
  if (!existing) throw new Error('Tutanak bulunamadı');
  await saveSummary({ ...existing, ...patch, id });
}

export async function deleteSummary(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE_NAME).delete(id);
  });
}
